import React, { useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, MenuItem, Select, InputLabel, FormControl, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Typography, Box } from '@mui/material';
import { Add, Edit, Delete, TravelExplore as ProspeccaoIcon } from '@mui/icons-material';
import { visitaProspeccaoService } from '../services/visitaProspeccaoService';
import { VisitaProspeccao } from '../types/visitaProspeccao';
import { clienteService } from '../services/clienteService';
import { colaboradorService } from '../services/colaboradorService';
import { apoliceService } from '../services/apoliceService';
import MainLayout from '../layouts/MainLayout';
import Pagination from '@mui/material/Pagination';

const statusOptions = ['Agendada', 'Realizada', 'Cancelada'];

type FormState = {
  id?: number;
  dataVisita: string;
  dataRetorno?: string;
  cpfCnpj?: string;
  nomeCliente?: string;
  observacao?: string;
  status: 'Agendada' | 'Realizada' | 'Cancelada';
  colaboradorId?: number;
  tipoVisita: 'online' | 'presencial';
  telefone?: string;
};

const initialFormState: FormState = {
  dataVisita: '',
  dataRetorno: '',
  cpfCnpj: '',
  nomeCliente: '',
  observacao: '',
  status: 'Agendada',
  colaboradorId: undefined,
  tipoVisita: 'presencial',
  telefone: '',
};

// Função para aplicar máscara de CPF/CNPJ
const aplicarMascaraCpfCnpj = (valor: string): string => {
  const apenasNumeros = valor.replace(/\D/g, '');
  
  if (apenasNumeros.length <= 11) {
    // CPF: 000.000.000-00
    return apenasNumeros
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  } else {
    // CNPJ: 00.000.000/0000-00
    return apenasNumeros
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  }
};

// Adicionar função de máscara de telefone
const aplicarMascaraTelefone = (valor: string): string => {
  const apenasNumeros = valor.replace(/\D/g, '');
  if (apenasNumeros.length <= 10) {
    // Formato: (99) 9999-9999
    return apenasNumeros
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  } else {
    // Formato: (99) 99999-9999
    return apenasNumeros
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2');
  }
};

const GerenciarVisitasProspeccao: React.FC = () => {
  const [visitas, setVisitas] = useState<VisitaProspeccao[]>([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(initialFormState);
  const [clientes, setClientes] = useState<{ id: number; nome: string }[]>([]);
  const [colaboradores, setColaboradores] = useState<{ id: number; nome: string }[]>([]);
  const [apolices, setApolices] = useState<{ id: number; numero: string }[]>([]);
  const [editId, setEditId] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);

  const fetchData = async (pagina = page) => {
    const result = await visitaProspeccaoService.getAll(pagina, limit);
    setVisitas(result.data);
    setTotal(result.total);
    setPage(result.page);
    setLimit(result.limit);
    const colaboradores = await colaboradorService.listar();
    setColaboradores(colaboradores);
  };

  useEffect(() => {
    fetchData(page);
  }, [page, limit]);

  const handleOpen = () => {
    setForm(initialFormState);
    setEditId(null);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setForm(initialFormState);
    setEditId(null);
  };

  const handleEdit = (visita: VisitaProspeccao) => {
    setForm({
      id: visita.id,
      dataVisita: visita.dataVisita.split('T')[0],
      dataRetorno: visita.dataRetorno ? visita.dataRetorno.split('T')[0] : '',
      cpfCnpj: visita.cpfCnpj ? aplicarMascaraCpfCnpj(visita.cpfCnpj) : '',
      nomeCliente: visita.nomeCliente?.toUpperCase() || '',
      observacao: visita.observacao?.toUpperCase() || '',
      status: visita.status,
      colaboradorId: visita.colaborador?.id,
      tipoVisita: visita.tipoVisita || 'presencial',
      telefone: visita.telefone || '',
    });
    setEditId(visita.id);
    setOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Deseja realmente excluir esta visita?')) {
      await visitaProspeccaoService.delete(id);
      fetchData();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload: any = {
      dataVisita: form.dataVisita,
      observacao: form.observacao,
      status: form.status,
      tipoVisita: form.tipoVisita,
    };
    if (form.dataRetorno) payload.dataRetorno = form.dataRetorno;
    if (form.cpfCnpj) payload.cpfCnpj = form.cpfCnpj;
    if (form.nomeCliente) payload.nomeCliente = form.nomeCliente;
    if (form.colaboradorId !== undefined) payload.colaboradorId = form.colaboradorId;
    if (form.telefone) payload.telefone = form.telefone;
    if (editId) {
      await visitaProspeccaoService.update(editId, payload);
    } else {
      await visitaProspeccaoService.create(payload);
    }
    handleClose();
    fetchData();
  };

  return (
    <MainLayout>
      <div style={{ padding: 24 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <ProspeccaoIcon sx={{ fontSize: '2.2rem' }} />
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 0 }}>
              Prospecção
            </Typography>
          </Box>
          <Button variant="contained" color="primary" startIcon={<Add />} onClick={handleOpen}>
            Nova Visita
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Data</TableCell>
                <TableCell>CPF/CNPJ</TableCell>
                <TableCell>Nome do Cliente</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Colaborador</TableCell>
                <TableCell>Data de Retorno</TableCell>
                <TableCell>Observação</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visitas.map((visita) => (
                <TableRow key={visita.id}>
                  <TableCell>{new Date(visita.dataVisita).toLocaleDateString()}</TableCell>
                  <TableCell>{visita.cpfCnpj ? aplicarMascaraCpfCnpj(visita.cpfCnpj) : '-'}</TableCell>
                  <TableCell>{visita.nomeCliente?.toUpperCase() || '-'}</TableCell>                  
                  <TableCell>{visita.status.toUpperCase()}</TableCell>
                  <TableCell>{visita.tipoVisita.toUpperCase()}</TableCell>
                  <TableCell>{visita.colaborador?.nome.toUpperCase()}</TableCell>
                  <TableCell>{visita.dataRetorno ? new Date(visita.dataRetorno).toLocaleDateString() : '-'}</TableCell>
                  <TableCell>{visita.observacao?.toUpperCase()}</TableCell>
                  <TableCell align="right">
                    <IconButton color="primary" onClick={() => handleEdit(visita)}><Edit /></IconButton>
                    <IconButton color="error" onClick={() => handleDelete(visita.id)}><Delete /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="select-limit-label">Tamanho da página</InputLabel>
            <Select
              labelId="select-limit-label"
              value={limit}
              label="Tamanho da página"
              onChange={e => { setLimit(Number(e.target.value)); setPage(1); }}
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </Select>
          </FormControl>
          <Pagination
            count={Math.ceil(total / limit)}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </Box>
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
          <DialogTitle>{editId ? 'Editar Visita' : 'Nova Visita'}</DialogTitle>
          <form onSubmit={handleSubmit}>
            <DialogContent>
              <FormControl fullWidth margin="normal">
                <InputLabel id="tipo-visita-label">Tipo de Visita</InputLabel>
                <Select
                  labelId="tipo-visita-label"
                  name="tipoVisita"
                  value={form.tipoVisita}
                  label="Tipo de Visita"
                  onChange={e => setForm(prev => ({ ...prev, tipoVisita: e.target.value as 'online' | 'presencial' }))}
                  required
                >
                  <MenuItem value="presencial">PRESENCIAL</MenuItem>
                  <MenuItem value="online">ONLINE</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Data da Visita"
                type="date"
                name="dataVisita"
                value={form.dataVisita}
                onChange={e => setForm(prev => ({ ...prev, dataVisita: e.target.value }))}
                fullWidth
                margin="normal"
                InputLabelProps={{ shrink: true }}
                required
              />
              <TextField
                label="CPF/CNPJ"
                name="cpfCnpj"
                value={form.cpfCnpj}
                onChange={e => setForm(prev => ({ ...prev, cpfCnpj: aplicarMascaraCpfCnpj(e.target.value) }))}
                fullWidth
                margin="normal"
                placeholder="000.000.000-00 ou 00.000.000/0000-00"
                inputProps={{
                  maxLength: 18 // Máximo para CNPJ com máscara (00.000.000/0000-00)
                }}
              />
              <TextField
                label="Nome do Cliente"
                name="nomeCliente"
                value={form.nomeCliente}
                onChange={e => {
                  const upperCaseValue = e.target.value.toUpperCase();
                  setForm(prev => ({ ...prev, nomeCliente: upperCaseValue }));
                }}
                fullWidth
                margin="normal"
              />
              <TextField
                label="Telefone"
                name="telefone"
                value={form.telefone}
                onChange={e => setForm(prev => ({ ...prev, telefone: aplicarMascaraTelefone(e.target.value) }))}
                fullWidth
                margin="normal"
                placeholder="(99) 99999-9999"
                inputProps={{ maxLength: 15 }}
              />
              <TextField
                label="Data de Retorno (se houver)"
                type="date"
                name="dataRetorno"
                value={form.dataRetorno}
                onChange={e => setForm(prev => ({ ...prev, dataRetorno: e.target.value }))}
                fullWidth
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />
              <FormControl fullWidth margin="normal">
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={form.status}
                  label="Status"
                  onChange={e => setForm(prev => ({ ...prev, status: e.target.value as FormState['status'] }))}
                  required
                >
                  {statusOptions.map((status) => (
                    <MenuItem key={status} value={status}>{status.toUpperCase()}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth margin="normal">
                <InputLabel>Colaborador</InputLabel>
                <Select
                  name="colaboradorId"
                  value={form.colaboradorId === undefined ? '' : String(form.colaboradorId)}
                  label="Colaborador"
                  onChange={e => setForm(prev => ({ ...prev, colaboradorId: e.target.value === '' ? undefined : Number(e.target.value) }))}
                  required
                >
                  {colaboradores.map((c) => (
                    <MenuItem key={c.id} value={String(c.id)}>{c.nome.toUpperCase()}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Observação"
                name="observacao"
                value={form.observacao}
                onChange={e => {
                  const upperCaseValue = e.target.value.toUpperCase();
                  setForm(prev => ({ ...prev, observacao: upperCaseValue }));
                }}
                fullWidth
                margin="normal"
                multiline
                minRows={2}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose}>Cancelar</Button>
              <Button type="submit" variant="contained" color="primary">Salvar</Button>
            </DialogActions>
          </form>
        </Dialog>
      </div>
    </MainLayout>
  );
};

export default GerenciarVisitasProspeccao; 